import { useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import {
  roomHasIntegrationToken,
  type Unit,
  type SchemeContent,
  type VilmerActivitiesResult,
} from "@shared/types/foyer";
import { foyerApi } from "../../http/foyer";
import { usePremiseScope } from "../../context/PremiseScopeContext";
import { PermissionGate, usePermission } from "../../auth/access";
import { activityDate, activityTimeRange } from "@shared/vilmer";
import type { Translate } from "../../i18n/translate";

interface Props {
  content: SchemeContent;
  /** The premise's rooms - the test button needs one to borrow a token from. */
  rooms: Unit[];
  onChange: (next: SchemeContent) => void;
}

/**
 * VilMer connection settings.
 *
 * Deliberately small, because the interesting half of this configuration is
 * NOT here: the API address is one value for the whole building, while the
 * integration token is per ROOM (Rooms → ⋯ → Integration token), because
 * VilMer issue tokens per room, per ward or per company and the room is the
 * only level that can carry all three. This pane says so out loud rather than
 * leaving an operator to find out from an empty TV.
 */
export default function VilmerEditor({ content, rooms, onChange }: Props) {
  const { t } = useTranslation("templates");
  const premiseId = usePremiseScope();
  const place = premiseId ? { premiseId } : undefined;
  const mayConfigure = usePermission("tv-layout", "administrate", place);
  const v = content.vilmer ?? {};
  const [url, setUrl] = useState(v.apiUrl ?? "");
  const [roomId, setRoomId] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<
    (VilmerActivitiesResult & { usingFixtures?: boolean }) | null
  >(null);

  const withToken = rooms.filter(roomHasIntegrationToken);
  const testRoom = roomId || withToken[0]?.id || "";

  const set = (part: Partial<typeof v>) =>
    onChange({ ...content, vilmer: { ...v, ...part } });

  const test = async () => {
    if (!premiseId || !testRoom) return;
    setBusy(true);
    setError(null);
    setResult(null);
    try {
      const out = await foyerApi.previewVilmer(premiseId, {
        url: url.trim() || undefined,
        roomId: testRoom,
      });
      setResult(out);
      // A successful read is also the operator's intent to use this address.
      // They still have to Save the premise for it to take effect.
      if (url.trim()) set({ apiUrl: url.trim() });
    } catch (err) {
      setError(
        vilmerErrorMessage(t, (err as { code?: string } | undefined)?.code)
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <Stack spacing={2} sx={{ maxWidth: 720 }}>
      <Typography variant="body2" color="text.secondary">
        {t("vilmerEditor.intro")}
      </Typography>

      <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack spacing={2}>
          <Box>
            <Typography
              variant="subtitle1"
              sx={{ fontWeight: "fontWeightSemibold" }}
            >
              {t("vilmerEditor.apiAddressTitle")}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              <Trans
                t={t}
                i18nKey="vilmerEditor.apiAddressHelp"
                components={{
                  code: (
                    <Box
                      component="code"
                      sx={{
                        fontFamily: "fontFamilyMono",
                        bgcolor: "action.hover",
                        px: 0.5,
                        borderRadius: 0.5,
                      }}
                    />
                  ),
                }}
              />
            </Typography>
          </Box>

          <Stack direction="row" spacing={1} alignItems="flex-start">
            <TextField
              size="small"
              fullWidth
              label={t("vilmerEditor.apiAddress")}
              placeholder="https://api.vilmer.no/foyer/activities"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              disabled={!mayConfigure}
              inputProps={{ "data-testid": "vilmer-api-url" }}
            />
            <TextField
              size="small"
              select
              label={t("vilmerEditor.refresh")}
              value={v.refreshMinutes ?? 30}
              onChange={(e) => set({ refreshMinutes: Number(e.target.value) })}
              disabled={!mayConfigure}
              sx={{ minWidth: 190 }}
              SelectProps={{ native: true }}
            >
              <option value={5}>{t("vilmerEditor.refreshEvery5Min")}</option>
              <option value={30}>{t("vilmerEditor.refreshEvery30Min")}</option>
              <option value={120}>
                {t("vilmerEditor.refreshEvery2Hours")}
              </option>
              <option value={1440}>{t("vilmerEditor.refreshDaily")}</option>
            </TextField>
          </Stack>

          <Divider />

          <Box>
            <Typography
              variant="subtitle1"
              sx={{ fontWeight: "fontWeightSemibold" }}
            >
              {t("vilmerEditor.unitTokens")}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              <Trans
                t={t}
                i18nKey="vilmerEditor.unitTokensHelp"
                components={{ strong: <strong /> }}
              />
            </Typography>
            <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
              <Chip
                size="small"
                color={withToken.length ? "success" : "default"}
                label={t("vilmerEditor.tokenCount", {
                  have: withToken.length,
                  total: rooms.length,
                })}
              />
            </Stack>
          </Box>

          <Stack direction="row" spacing={1} alignItems="flex-start">
            <TextField
              size="small"
              select
              label={t("vilmerEditor.testWithUnit")}
              value={testRoom}
              onChange={(e) => setRoomId(e.target.value)}
              sx={{ minWidth: 200 }}
              SelectProps={{ native: true }}
              disabled={!withToken.length}
            >
              {withToken.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.id}
                  {r.name ? ` · ${r.name}` : ""}
                </option>
              ))}
            </TextField>
            <PermissionGate
              resource="tv-layout"
              level="administrate"
              place={place}
            >
              <Button
                variant="contained"
                disabled={busy || !premiseId || !testRoom}
                onClick={test}
                startIcon={busy ? <CircularProgress size={16} /> : undefined}
                sx={{ whiteSpace: "nowrap" }}
                data-testid="vilmer-test"
              >
                {t("vilmerEditor.testConnection")}
              </Button>
            </PermissionGate>
          </Stack>

          {!premiseId && (
            <Alert severity="info">{t("vilmerEditor.saveFirst")}</Alert>
          )}
          {premiseId && !withToken.length && (
            <Alert severity="warning">{t("vilmerEditor.noTokens")}</Alert>
          )}

          {error && <Alert severity="error">{error}</Alert>}

          {result && (
            <Alert severity={result.usingFixtures ? "warning" : "success"}>
              {result.usingFixtures ? (
                <Trans
                  t={t}
                  i18nKey="vilmerEditor.sampleData"
                  values={{ count: result.activities.length }}
                  components={{ strong: <strong /> }}
                />
              ) : result.unit?.name ? (
                <Trans
                  t={t}
                  i18nKey="vilmerEditor.connectedFor"
                  values={{
                    count: result.activities.length,
                    unit: result.unit.name,
                  }}
                  components={{ strong: <strong /> }}
                />
              ) : (
                <Trans
                  t={t}
                  i18nKey="vilmerEditor.connected"
                  values={{ count: result.activities.length }}
                  components={{ strong: <strong /> }}
                />
              )}
              {result.activities.length > 0 && (
                <Box component="ul" sx={{ m: "8px 0 0", pl: 2.5 }}>
                  {result.activities.slice(0, 5).map((a) => (
                    <li key={a.id}>
                      <Typography variant="body2" component="span">
                        {`${activityDate(a)} ${activityTimeRange(a)} — ${a.title}`}
                        {a.location ? ` (${a.location})` : ""}
                        {a.cancelled ? ` · ${t("vilmerEditor.cancelled")}` : ""}
                      </Typography>
                    </li>
                  ))}
                </Box>
              )}
            </Alert>
          )}
        </Stack>
      </Paper>
    </Stack>
  );
}

/** Coded reason → something an operator can act on. Anything unrecognised
 *  stays honest about being unrecognised rather than guessing. */
function vilmerErrorMessage(t: Translate, code: string | undefined): string {
  switch (code) {
    case "vilmer_bad_url":
      return t("templates:vilmerEditor.errors.badUrl");
    case "vilmer_blocked_host":
      return t("templates:vilmerEditor.errors.blockedHost");
    case "vilmer_unauthorized":
      return t("templates:vilmerEditor.errors.unauthorized");
    case "vilmer_unparseable":
      return t("templates:vilmerEditor.errors.unparseable");
    case "vilmer_too_large":
      return t("templates:vilmerEditor.errors.tooLarge");
    case "vilmer_timeout":
      return t("templates:vilmerEditor.errors.timeout");
    case "vilmer_unsaved_url_needs_token":
      return t("templates:vilmerEditor.errors.unsavedUrlNeedsToken");
    case "vilmer_no_token":
      return t("templates:vilmerEditor.errors.noToken");
    default:
      return t("templates:vilmerEditor.errors.unreachable");
  }
}
